// "सम्पूर्ण रूप": every form of the chosen root in this tense, one card per person and number.
// सामान्य: the book's standard form with each of its pronouns. विकल्प ("विकल्प · विस्तृत रूप", after the reference
// design): the notes' pronouns (अहं, हं, अम्मि …), every ending as the notes write it (मि / आमि / एमि) and every pronoun
// paired with every form (अहं हसमि, अहं हसामि, अहं हसेमि, हं हसमि …), each to tap and hear.

import type { FC, ReactNode } from 'react'
import type { Num, Person } from '@/prakrit/lib/grammar/types'
import type { PratyayaForm, PratyayaTable } from '@/prakrit/lib/grammar/pratyaya'
import type { KalMode } from '@/prakrit/components/grammar/kal/kal-tabs'

import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NUMBERS, PERSONS } from '@/prakrit/lib/grammar/types'
import { NUMBER_LABEL, PERSON_LABEL } from '@/prakrit/lib/grammar/labels'
import { fullEndings } from '@/prakrit/lib/grammar/pratyaya'
import { hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'
import { PANEL } from '@/prakrit/components/grammar/dhatu/dhatu-ui'
import { PrakritWord } from '@/prakrit/components/common/prakrit-word'
import { AudioButton } from '@/prakrit/components/common/audio-button'
import { DhatuPicture } from '@/prakrit/components/common/dhatu-picture'
import { PLAIN_SUBJECTS, subjectsOf } from '@/prakrit/components/grammar/kal/kal-data'

interface FormsGridProps {
    table: PratyayaTable
    mode: KalMode
    root: string
    /** "वर्तमान काल" for the heading: "वर्तमान काल — हस धातु के विकल्प रूप". */
    tenseHi: string
    tenseEn: string
}

interface Line {
    key: string
    subject: string
    form: string
}

/** Every pronoun with every form, pronoun by pronoun (अहं हसमि, अहं हसामि … हं हसमि …), as the notes list them. */
const everyPair = (subjects: string[], forms: PratyayaForm[]): Line[] => (
    subjects.flatMap(subject => forms.map(f => ({ key: `${subject}|${f.form}`, subject, form: f.form })))
)

/** सामान्य: one sentence per pronoun when the cell has one form, else one per form with the first pronoun. */
const samanyaLines = (subjects: string[], forms: PratyayaForm[]): Line[] => {
    if (forms.length === 1) return everyPair(subjects, forms)

    const subject = subjects[0] ?? ''

    return forms.map(f => ({ key: `${subject}|${f.form}`, subject, form: f.form }))
}

const Label: FC<{ hi: string, en: string }> = ({ hi, en }): ReactNode => (
    <p className='flex flex-wrap items-baseline gap-x-1.5 text-xs text-muted-foreground'>
        <span lang='hi' className='hindi font-medium text-foreground/80'>{hi}</span>
        <span aria-hidden='true'>·</span>
        <span lang='en'>{en}</span>
    </p>
)

const Word: FC<{ word: string }> = ({ word }): ReactNode => <PrakritWord word={word} plain={PLAIN_SUBJECTS.has(word)} />

/** How many sentences a card lists before "show all". */
const SHOWN = 9

const CellCard: FC<{ p: Person, n: Num, table: PratyayaTable, mode: KalMode, root: string }> = ({ p, n, table, mode, root }): ReactNode => {
    const [open, setOpen] = useState(false)
    const cell = table[p][n]
    const s = subjectsOf(p, n)
    const vikalp = mode === 'vikalp'
    const forms = vikalp ? cell.all : cell.samanya
    const subjects = vikalp ? s.vikalp : s.book
    const lines = vikalp ? everyPair(subjects, forms) : samanyaLines(subjects, forms)
    const shown = open ? lines : lines.slice(0, SHOWN)
    const hidden = lines.length - shown.length
    const guessed = vikalp && cell.vikalp.length > 0 && cell.vikalp.every(f => f.source === 'pattern')
    const headingId = `roop-${mode}-${p}-${n}`

    return (
        <article aria-labelledby={headingId} className={cn(PANEL, 'flex flex-col gap-4 p-5 sm:p-6')}>
            <header className='flex items-start justify-between gap-3'>
                <h4 id={headingId} className='flex flex-col leading-tight'>
                    <span lang='hi' className='text-lg font-bold'>{PERSON_LABEL[p].hi}</span>
                    <span lang='en' className='font-sans text-xs text-muted-foreground'>{PERSON_LABEL[p].en}</span>
                </h4>
                <span className='rounded-full bg-secondary px-3 py-1 text-xs font-medium'>
                    <span lang='hi' className='hindi'>{NUMBER_LABEL[n].hi}</span>
                    <span className='sr-only'> · {NUMBER_LABEL[n].en}</span>
                </span>
            </header>

            <div className='flex flex-col gap-2'>
                <Label hi={`${root} धातु · सर्वनाम`} en='subject pronouns' />
                <p className='flex flex-wrap gap-2'>
                    {subjects.map(subject => (
                        <span key={subject} lang='pra' className='inline-flex min-h-12 items-center rounded-full border-2 border-border bg-background px-5 font-prakrit text-xl font-semibold'>
                            {subject}
                        </span>
                    ))}
                </p>
            </div>

            <div className='flex flex-col gap-2'>
                <Label hi='प्रत्यय' en='ending' />
                <p lang='pra' className='rounded-2xl bg-secondary/80 px-4 py-3 font-prakrit text-lg font-semibold leading-relaxed'>
                    {fullEndings(forms).join(' / ')}
                </p>
                {vikalp && (
                    <p className='text-xs text-muted-foreground'>
                        <span lang='hi' data-ex='hi' className='hindi'>
                            {hiDigits(subjects.length)} सर्वनाम × {hiDigits(forms.length)} रूप = {hiDigits(lines.length)} वाक्य
                        </span>
                        <span aria-hidden='true' className='ex-sep'> · </span>
                        <span lang='en' data-ex='en'>{subjects.length} pronouns × {forms.length} forms = {lines.length} sentences</span>
                    </p>
                )}
                {guessed && (
                    <p className='text-xs text-muted-foreground'>
                        <span lang='hi' data-ex='hi' className='hindi'>यह खाना नोट्स में अभी नहीं है; विकल्प उसी नियम से बनाए गए हैं।</span>
                        <span aria-hidden='true' className='ex-sep'> · </span>
                        <span lang='en' data-ex='en'>This cell is not in the notes yet; its variants are built by the same rule.</span>
                    </p>
                )}
            </div>

            {lines.length > 0 && (
                <ul className='flex flex-col divide-y divide-border border-t border-border'>
                    {shown.map(line => (
                        <li key={line.key} className='flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 py-3'>
                            <span lang='pra' className='pra pra-md font-semibold text-saffron-strong'>
                                <Word word={line.subject} /> <Word word={line.form} />
                            </span>
                            <AudioButton text={`${line.subject} ${line.form}`} size='sm' />
                        </li>
                    ))}
                </ul>
            )}

            {lines.length > SHOWN && (
                <button
                    type='button'
                    aria-expanded={open}
                    onClick={() => setOpen(v => !v)}
                    className='inline-flex min-h-tap items-center gap-1.5 self-start text-sm font-semibold text-saffron-strong hover:underline'
                >
                    <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} aria-hidden='true' />
                    <span lang='hi' className='hindi'>{open ? 'कम दिखाएँ' : `सभी ${hiDigits(lines.length)} वाक्य दिखाएँ`}</span>
                    <span aria-hidden='true'>·</span>
                    <span lang='en'>{open ? 'Show fewer' : `Show all ${lines.length} (${hidden} more)`}</span>
                </button>
            )}
        </article>
    )
}

export const FormsGrid: FC<FormsGridProps> = ({ table, mode, root, tenseHi, tenseEn }): ReactNode => {
    const vikalp = mode === 'vikalp'

    return (
        <div className='flex flex-col gap-5 rounded-[2rem] border border-saffron/35 bg-saffron/5 p-4 sm:p-6'>
            <div className='flex items-start gap-4'>
                <DhatuPicture key={root} root={root} decorative sizes='96px' className='hidden size-20 shrink-0 rounded-2xl shadow-card motion-safe:animate-in motion-safe:fade-in motion-safe:duration-500 min-[420px]:block sm:size-24' />
                <div className='flex min-w-0 flex-col gap-2'>
                    <span className='inline-flex w-fit items-center rounded-full bg-primary px-3.5 py-1 text-sm font-semibold text-primary-foreground'>
                        <span lang='hi' className='hindi'>{vikalp ? 'विकल्प · विस्तृत रूप' : 'सामान्य दृश्य'}</span>
                        <span className='sr-only'>{vikalp ? ' · variants, every form' : ' · standard view'}</span>
                    </span>
                    <h3 className='flex flex-col gap-0.5'>
                        <span lang='hi' className='text-xl font-bold sm:text-2xl'>{tenseHi} — {root} धातु के {vikalp ? 'विकल्प' : 'सामान्य'} रूप</span>
                        <span lang='en' className='font-sans text-sm font-medium text-muted-foreground'>{tenseEn}: {vikalp ? 'every variant' : 'the standard forms'} of {root}</span>
                    </h3>
                    <p className='text-sm text-muted-foreground'>
                        <span lang='hi' data-ex='hi' className='hindi'>हर सर्वनाम के साथ {root} धातु के दर्ज {tenseHi} रूप।</span>
                        <span aria-hidden='true' className='ex-sep'> · </span>
                        <span lang='en' data-ex='en'>Every listed form of {root} with each pronoun. Tap a word for its grammar.</span>
                    </p>
                </div>
            </div>
            <div className='grid gap-4 lg:grid-cols-2'>
                {PERSONS.flatMap(p => NUMBERS.map(n => <CellCard key={`${p}.${n}`} p={p} n={n} table={table} mode={mode} root={root} />))}
            </div>
        </div>
    )
}
